import { useDispatch, useSelector } from "react-redux";

import type {
    BaseActivatedPanelProps,
    SelectionDropdownItem } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import {
    ManualSelectionItem
} from "app/pages/sales-intelligence/common-components/table-actions-panel";
import {
    selectActiveLeadsType,
    selectSelectedRowDomains,
    setIsRowSelectionEnabled,
    useClearAndCloseRowsSelection,
} from "app/pages/sales-intelligence/modules/leads-search";
import { addLeadsToListThunk } from "app/pages/sales-intelligence/modules/leads-search/store/effects";
import trackingService from "app/pages/sales-intelligence/services/tracking/createMultiSelectorPanelService";
import SaveToListPanel from "app/pages/sales-intelligence/sub-modules/opportunities/components/table-actions-panel/SaveToListPanel/SaveToListPanel";
import useTrackedWebsitesQuota from "app/pages/sales-intelligence/sub-modules/opportunities/hooks/useTrackedWebsitesQuota";
import saveToListSelectionDropdown from "app/pages/sales-intelligence/sub-modules/opportunities/models/save-to-list-selection/saveToListSelectionDropdown";
import {
    selectOpportunityListCreating,
    selectOpportunityListUpdating,
} from "app/pages/sales-intelligence/sub-modules/opportunities/store/selectors";
import type { OpportunityListType } from "app/pages/sales-intelligence/sub-modules/opportunities/types";

import { useLeadsResultsActionsState } from "../../../query/queries/useLeadsResultsActionsState";
import { addTopNLeadsToListThunk } from "../../../store/effects";

const SaveToListPanelContainer = (props: BaseActivatedPanelProps) => {
    const dispatch = useDispatch();

    const selectedRowDomains = useSelector(selectSelectedRowDomains);
    const activeLeadsType = useSelector(selectActiveLeadsType);
    const isListCreateInProgress = useSelector(selectOpportunityListCreating);
    const isListUpdateInProgress = useSelector(selectOpportunityListUpdating);

    const { areResultsFetching, areActionsDisabled, numberOfResults } = useLeadsResultsActionsState(activeLeadsType);

    const { remaining } = useTrackedWebsitesQuota();

    const clearAndCloseRowsSelection = useClearAndCloseRowsSelection();

    const handleDeactivate = (): void => {
        clearAndCloseRowsSelection();
        props.onDeactivate();
    };

    const handleSelectionChange = (item: Readonly<SelectionDropdownItem>): void => {
        if (item.id === ManualSelectionItem.ID) {
            dispatch(setIsRowSelectionEnabled(true));

            return;
        }

        clearAndCloseRowsSelection();
    };

    const handleSubmit = async (
        list: Readonly<OpportunityListType>,
        selectionItem: Readonly<SelectionDropdownItem>,
    ): Promise<void> => {
        trackingService.trackMultiSelectorSaveToListClick(remaining, selectionItem);

        if (selectionItem.id === ManualSelectionItem.ID) {
            await dispatch(addLeadsToListThunk(list, selectedRowDomains));
        } else {
            await dispatch(addTopNLeadsToListThunk({ list, domainsCount: selectionItem.value }));
        }

        handleDeactivate();
    };

    return (
        <SaveToListPanel
            isDisabled={areActionsDisabled}
            isLoading={areResultsFetching || isListCreateInProgress || isListUpdateInProgress}
            isSubmitInProgress={isListCreateInProgress || isListUpdateInProgress}
            numberOfRemainingItems={remaining}
            numberOfResults={numberOfResults}
            numberOfSelectedItems={selectedRowDomains.length}
            selectionDropdown={saveToListSelectionDropdown}
            onDeactivate={handleDeactivate}
            onSelectionChange={handleSelectionChange}
            onSubmit={handleSubmit}
        />
    );
};

export default SaveToListPanelContainer;
